import { generateSearchQuery } from "@/utils/search";
import { IOnboardingAccount, IOnboardingForm } from "../types";
import { CUSTOMER_TYPE_TRADE_COUNTER, CREDIT_TERMS_UPFRONT, ACCOUNT_DELETED_STATUSCODE } from "../constants";

/**
 * Ensures phone number is formatted with hyphen between country code and number
 * Example: +443333333333 -> +44-3333333333
 */
export const formatPhoneNumber = (phone?: string): string | undefined => {
  if (!phone) return phone;

  // Remove all spaces, hyphens, parentheses
  const cleaned = phone.replace(/[\s\-()]/g, '');

  // Check if already has proper format +XX-XXXXXXX
  if (/^\+\d{1,3}-\d+$/.test(phone)) {
    return phone;
  }

  // Match pattern: +{1-3 digits}{remaining digits}
  // Try common country code lengths
  const patterns = [
    /^\+(\d{1})(\d{10,})$/,   // 1-digit codes (US: +1)
    /^\+(\d{2})(\d{8,})$/,    // 2-digit codes (GB: +44, AU: +61)
    /^\+(\d{3})(\d{7,})$/,    // 3-digit codes (UAE: +971, SA: +966)
  ];

  for (const pattern of patterns) {
    const match = cleaned.match(pattern);
    if (match) {
      return `+${match[1]}-${match[2]}`;
    }
  }

  // If no pattern matches, return original
  return phone;
};

/**
 * Generate search query for onboarding accounts
 */
export const generateOnboardingSearchQuery = (searchText: string): string => {
  return generateSearchQuery(searchText, [
    "name",
    "dogma_tradingname",
    "emailaddress1",
    "telephone1",
  ]);
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmailSearchText = (searchText: string): boolean =>
  EMAIL_PATTERN.test(searchText.trim());

/**
 * Build the query string appended to /AccountApi for the onboarding list.
 * A full email search bypasses the generic contains-search entirely and
 * hits the dedicated contact-email lookup: /AccountApi?contactEmail=...
 * Status/region filters still combine as a normal $filter alongside it.
 */
export const buildOnboardingFilterQuery = ({
  searchText,
  status,
  region,
}: {
  searchText: string;
  status: number | null;
  region: number | null;
}): string => {
  const statusQuery = generateStatusFilterQuery(status);
  const regionQuery = generateRegionFilterQuery(region);
  const combinedODataFilter = combineFilterQueries([statusQuery, regionQuery]);
  const odataFilterPart = combinedODataFilter ? `&$filter=${combinedODataFilter}` : '';

  const trimmedSearch = searchText.trim();
  // SearchInput URI-encodes the raw text before calling onChange (e.g. "@" -> "%40"),
  // so decode before checking/using it as an email.
  let decodedSearch = trimmedSearch;
  try { decodedSearch = decodeURIComponent(trimmedSearch); } catch { /* not encoded, use as-is */ }

  if (decodedSearch && isEmailSearchText(decodedSearch)) {
    return `&contactEmail=${encodeURIComponent(decodedSearch)}${odataFilterPart}`;
  }

  const searchQuery = trimmedSearch ? generateOnboardingSearchQuery(trimmedSearch)?.replace('&$filter=', '') : '';
  const combinedFilter = combineFilterQueries([searchQuery ? `(${searchQuery})` : searchQuery, statusQuery, regionQuery]);
  return combinedFilter ? `&$filter=${combinedFilter}` : '';
};

/**
 * Combine multiple filter queries with 'and' operator
 */
export const combineFilterQueries = (filters: string[]): string => {
  const validFilters = filters.filter(f => f && f.trim() !== '');
  return validFilters.length > 0 ? validFilters.join(' and ') : '';
};

/**
 * Generate status filter query for onboarding accounts
 */
export const generateStatusFilterQuery = (statuscode: number | null | undefined): string => {
  if (statuscode === null || statuscode === undefined) {
    return "";
  }
  if (statuscode === ACCOUNT_DELETED_STATUSCODE) {
    return `statecode eq 1`;
  }
  return `statuscode eq ${statuscode}`;
};

/**
 * Generate region filter query for onboarding accounts
 */
export const generateRegionFilterQuery = (region: number | null | undefined): string => {
  if (region === null || region === undefined) {
    return "";
  }
  return `dogma_region eq ${region}`;
};

/**
 * Transform onboarding account data for form initialization
 */
export const transformOnboardingToForm = (record: IOnboardingAccount): IOnboardingForm => {
  return {
    "Company Name": record.name,
    "Trading Name": record.dogma_tradingname,
    "Prefix": record.dogma_prefix || "",
    "Primary Contact Name": "", // This comes from contact, not account
    "Primary Contact Email": record.emailaddress1 || "",
    "Primary Contact Phone": record.telephone1 || "",
    "Company Address": record.address1_composite || "",
    Region: record.dogma_region || 1,
    "Customer Type": record.dogma_customertype || CUSTOMER_TYPE_TRADE_COUNTER,
    "Discount % from VS Base Price": record.dogma_discountpercentage || 0,
    "Quote Validity (days)": record.dogma_quotevaliditydays || 30,
    "Credit Terms": record.dogma_creditterms || CREDIT_TERMS_UPFRONT,
    "Assigned RSM": record.dogma_assignedrsm,
    "Currency": record._dogma_dealerscurrency_value,
  };
};

/**
 * Transform form data for Dataverse update API submission
 */
export const transformFormForApi = (
  values: IOnboardingForm
): Record<string, any> => {
  return {
    name: values["Company Name"],
    dogma_tradingname: values["Trading Name"],
    dogma_prefix: values["Prefix"],
    emailaddress1: values["Primary Contact Email"],
    telephone1: formatPhoneNumber(values["Primary Contact Phone"]),
    address1_composite: values["Company Address"],
    dogma_region: values["Region"],
    dogma_customertype: values["Customer Type"],
    dogma_discountpercentage: values["Discount % from VS Base Price"],
    dogma_quotevaliditydays: values["Quote Validity (days)"],
    dogma_creditterms: values["Credit Terms"],
    dogma_assignedrsm: values["Assigned RSM"],
    ...(values["Currency"] ? { "Dealers Currency": values["Currency"] } : {}),
  };
};


